// One registry module across the fleet: its version distribution, how current
// each call is, and every job/env/region that runs it.

import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Link as MuiLink,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { ViewModule as ModulesIcon } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import PageHeader from '../../components/PageHeader';
import EmptyState from '../../components/EmptyState';
import { useFleetModules } from '../../hooks/useFleetModules';
import { getColors, rounded } from '../../theme/tokens';
import { jobPath } from '../../lib/jobPath';
import { Lag, moduleName, modulePath, ModuleSummary, ModuleUsage } from '../../lib/fleetModules';
import { sortByEnvRegion } from '../../lib/rollbackProfiles';
import {
  BarList,
  ChartCard,
  FleetLoadNote,
  LAG_HUES,
  LAG_LABELS,
  LagBar,
  StatTile,
  versionHue,
} from '../../components/infra/charts';

interface CallRow {
  app: string;
  jobId: string;
  key: string;
  declaredIn: string;
  direct: boolean;
  constraint: string;
  subpath: string;
  byEnv: Map<string, ModuleUsage>;
}

const ModulePage: React.FC = () => {
  const theme = useTheme();
  const mode = theme.palette.mode;
  const c = getColors(mode);
  const source = useParams()['*'] || '';
  const { fleet, progress, loading, registry } = useFleetModules();
  const module = fleet.modules.find(m => m.source === source);

  // One row per job module call, one column per env/region.
  const { calls, envColumns } = useMemo(() => {
    const rows = new Map<string, CallRow>();
    const envs = new Set<string>();
    (module?.usages || []).forEach(u => {
      envs.add(u.envRegion);
      const k = `${u.app}/${u.jobId}/${u.key}/${u.declaredIn}`;
      if (!rows.has(k)) {
        rows.set(k, {
          app: u.app,
          jobId: u.jobId,
          key: u.key,
          declaredIn: u.declaredIn,
          direct: u.direct,
          constraint: u.constraint,
          subpath: u.subpath,
          byEnv: new Map(),
        });
      }
      rows.get(k)!.byEnv.set(u.envRegion, u);
    });
    const envColumns = sortByEnvRegion(Array.from(envs).map(env_region => ({ env_region }))).map(e => e.env_region);
    const calls = Array.from(rows.values()).sort(
      (a, b) => Number(b.direct) - Number(a.direct) || a.app.localeCompare(b.app) || a.jobId.localeCompare(b.jobId) || a.key.localeCompare(b.key)
    );
    return { calls, envColumns };
  }, [module]);

  const crumbs = [
    { label: 'Infrastructure', to: '/infra' },
    { label: 'Modules', to: '/infra/modules' },
    { label: module?.name || moduleName(source) },
  ];

  if (!module) {
    return (
      <Box>
        <PageHeader title={moduleName(source)} crumbs={crumbs} subtitle={source} />
        <FleetLoadNote loaded={progress.loaded} total={progress.total} loading={loading} registry={registry} />
        {!loading && (
          <EmptyState icon={<ModulesIcon />} title="Module not deployed" body={`No terraform job reports ${source}.`} />
        )}
      </Box>
    );
  }

  const hues = LAG_HUES[mode];
  const lagChip = (lag: Lag, version: string) => (
    <Tooltip arrow title={LAG_LABELS[lag]}>
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.6,
          fontFamily: 'monospace',
          fontSize: '0.8rem',
          color: c.inkSecondary,
          whiteSpace: 'nowrap',
        }}
      >
        <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: hues[lag], flexShrink: 0 }} />
        {version}
      </Box>
    </Tooltip>
  );

  const directShare = module.directUsages > 0 ? module.lagCounts.current / module.directUsages : 1;

  return (
    <Box>
      <PageHeader
        title={module.name}
        crumbs={crumbs}
        subtitle={
          <Box component="span" sx={{ fontFamily: 'monospace' }}>
            {module.source}
          </Box>
        }
      />
      <FleetLoadNote loaded={progress.loaded} total={progress.total} loading={loading} registry={registry} />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(5, 1fr)' }, gap: 1.5 }}>
          <StatTile label="Jobs" value={module.jobs.length} hint={`${module.apps.length} apps`} />
          <StatTile label="Calls" value={module.usages.length} hint={`${module.directUsages} direct`} />
          <StatTile label="Versions" value={module.versions.length} />
          <StatTile
            label="Latest in Spacelift"
            value={module.latestSource === 'registry' ? module.newest : '—'}
            hint={module.latestSource === 'registry' ? `newest deployed ${module.newestDeployed}` : module.registryError || 'not checked yet'}
          />
          <StatTile label="On latest" value={`${Math.round(directShare * 100)}%`} hint="of direct calls" />
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 2.5 }}>
          <ChartCard title="Calls by version" subtitle="Every call in every env/region, newest first">
            <BarList
              labelWidth={120}
              rows={module.versions.map((v, i) => ({
                key: v.version,
                label: v.version,
                sublabel: `${v.jobs} job${v.jobs === 1 ? '' : 's'} · ${LAG_LABELS[v.lag].toLowerCase()}`,
                value: v.usages,
                segments: [
                  { key: v.version, label: v.version, value: v.usages, color: versionHue(mode, i) },
                ],
              }))}
            />
          </ChartCard>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <ChartCard
              title="How current"
              subtitle={
                module.latestSource === 'registry'
                  ? `Direct calls against ${module.newest}, the latest release in Spacelift`
                  : `Direct calls against ${module.newest}, the newest version deployed (Spacelift didn't answer)`
              }
            >
              {module.directUsages > 0 ? (
                <LagBar counts={module.lagCounts} />
              ) : (
                <Typography variant="body2" sx={{ color: c.inkMuted }}>
                  Only called from inside other modules, which pin its version.
                </Typography>
              )}
            </ChartCard>
            <ChartCard title="Releases" subtitle="Published in Spacelift, newest first, with what the fleet runs of each">
              <ReleaseList module={module} />
            </ChartCard>
            <ChartCard title="Composition">
              <Box sx={{ display: 'grid', gridTemplateColumns: '120px 1fr', rowGap: 1, alignItems: 'baseline' }}>
                <Typography variant="body2" sx={{ color: c.inkMuted }}>
                  Called by
                </Typography>
                <ModuleChips sources={module.parents} empty="Stack roots only" />
                <Typography variant="body2" sx={{ color: c.inkMuted }}>
                  Calls
                </Typography>
                <ModuleChips sources={module.children} empty="No registry modules" />
                <Typography variant="body2" sx={{ color: c.inkMuted }}>
                  Constraints
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', color: c.inkSecondary }}>
                  {module.constraints.join(' · ') || '—'}
                </Typography>
                {module.subpaths.length > 0 && (
                  <>
                    <Typography variant="body2" sx={{ color: c.inkMuted }}>
                      Submodules
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', color: c.inkSecondary }}>
                      {module.subpaths.map(s => `//${s}`).join(' · ')}
                    </Typography>
                  </>
                )}
              </Box>
            </ChartCard>
          </Box>
        </Box>

        <ChartCard
          title="Deployed calls"
          subtitle={`${calls.length} module calls across ${module.jobs.length} jobs. The dot shows how far each version trails ${module.newest}.`}
        >
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Job</TableCell>
                  <TableCell>Call</TableCell>
                  <TableCell>Constraint</TableCell>
                  {envColumns.map(e => (
                    <TableCell key={e} sx={{ whiteSpace: 'nowrap' }}>
                      {e}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {calls.map(row => {
                  const versions = new Set(Array.from(row.byEnv.values()).map(u => u.version));
                  return (
                    <TableRow
                      key={`${row.app}/${row.jobId}/${row.key}/${row.declaredIn}`}
                      sx={versions.size > 1 ? { backgroundColor: c.hoverWash } : undefined}
                    >
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <MuiLink component={RouterLink} to={jobPath(row.app, row.jobId, 'terraform')} underline="hover">
                          {row.app}/{row.jobId}
                        </MuiLink>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontSize: '0.85rem', color: c.ink }}>
                          {row.key}
                          {row.subpath && (
                            <Box component="span" sx={{ color: c.inkFaint }}>
                              {` //${row.subpath}`}
                            </Box>
                          )}
                        </Typography>
                        {!row.direct && (
                          <Typography variant="caption" sx={{ color: c.inkFaint }}>
                            inside {row.declaredIn}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem', color: c.inkMuted, whiteSpace: 'nowrap' }}>
                        {row.constraint || '—'}
                      </TableCell>
                      {envColumns.map(e => {
                        const u = row.byEnv.get(e);
                        return (
                          <TableCell key={e}>
                            {u ? lagChip(u.lag, u.version) : <Typography sx={{ color: c.inkFaint }}>—</Typography>}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        </ChartCard>
      </Box>
    </Box>
  );
};

const RELEASES_SHOWN = 12;

const ReleaseList: React.FC<{ module: ModuleSummary }> = ({ module }) => {
  const theme = useTheme();
  const c = getColors(theme.palette.mode);
  const [all, setAll] = useState(false);
  if (module.published.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: c.inkMuted }}>
        {module.registryError ? `Spacelift: ${module.registryError}.` : 'No releases from Spacelift yet.'}
      </Typography>
    );
  }
  const deployed = new Map(module.versions.map(v => [v.version, v]));
  const shown = all ? module.published : module.published.slice(0, RELEASES_SHOWN);
  return (
    <Box>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Release</TableCell>
            <TableCell align="right">Jobs</TableCell>
            <TableCell align="right">Calls</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {shown.map(v => {
            const d = deployed.get(v);
            return (
              <TableRow key={v}>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem', color: d ? c.ink : c.inkFaint }}>
                  {v}
                  {v === module.newest && (
                    <Chip size="small" label="latest" sx={{ ml: 1, height: 18, fontSize: '0.7rem', borderRadius: `${rounded.xs}px` }} />
                  )}
                </TableCell>
                <TableCell align="right" sx={{ color: d ? c.inkSecondary : c.inkFaint }}>
                  {d ? d.jobs : '—'}
                </TableCell>
                <TableCell align="right" sx={{ color: d ? c.inkSecondary : c.inkFaint }}>
                  {d ? d.usages : '—'}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      {module.published.length > RELEASES_SHOWN && (
        <Button size="small" sx={{ mt: 1 }} onClick={() => setAll(a => !a)}>
          {all ? 'Show fewer' : `Show all ${module.published.length}`}
        </Button>
      )}
    </Box>
  );
};

const ModuleChips: React.FC<{ sources: string[]; empty: string }> = ({ sources, empty }) => {
  const theme = useTheme();
  const c = getColors(theme.palette.mode);
  if (sources.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: c.inkFaint }}>
        {empty}
      </Typography>
    );
  }
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
      {[...sources].sort((a, b) => moduleName(a).localeCompare(moduleName(b))).map(s => (
        <Chip
          key={s}
          size="small"
          clickable
          component={RouterLink}
          to={modulePath(s)}
          label={moduleName(s)}
          title={s}
          sx={{ borderRadius: `${rounded.sm}px` }}
        />
      ))}
    </Box>
  );
};

export default ModulePage;
